/*
 * The site's own words in US English - the default language (S48, 2026-10-29).
 *
 * Every line here is the text the site showed before it had a second language,
 * moved out of the components unchanged: the owner's screenshots of 2026-10-29
 * marked what the English wording is, and it is what the site already said. A
 * change of wording is made here, and the Danish beside it in da.ts.
 *
 * THE SHAPE IS THE CONTRACT. `Messages` is this object's type, and da.ts is
 * declared as one, so a line added here and not there - or the other way round -
 * fails the typecheck rather than showing English on a Danish page. Lines that
 * take a number or a name are functions, so each language decides its own plural
 * and word order; lines with emphasis or code inside them use the marks
 * richText.tsx reads.
 *
 * Not here, by decision: anything an author or administrator wrote (article and
 * release-note text, titles, product and category names, the page headings on
 * the Settings page) and the companies' footer links and straplines. S48 first
 * covered the reader's site, the header and footer both shells share, and the
 * sign-in cards in front of the admin pages; the admin pages themselves followed
 * the same day at the owner's request, as `admin` - one pair of files per part of
 * the admin site in i18n/admin/, gathered here.
 */

import { attachmentFileTypeForName, formatByteSize } from '@accotool-kb/shared';
import { rulesEn } from './admin/rules.en.js';
import { shellEn } from './admin/shell.en.js';
import { articlesEn } from './admin/articles.en.js';
import { editorEn } from './admin/editor.en.js';
import { releaseNotesEn } from './admin/releaseNotes.en.js';
import { siteEn } from './admin/site.en.js';

export type GrantedVia = 'app-role' | 'groups-claim' | 'microsoft-graph';

export const en = {
  common: {
    /** The spinner's hidden name, when its caller gives none. */
    loadingLabel: 'Loading',
    loading: 'Loading…',
    somethingWentWrong: 'Something went wrong.',
    tryAgain: 'Try again',
    dismiss: 'Dismiss',
    close: 'Close',
    concurrencyConflict: 'Reload the page to pick up the other change, then make yours again.',
    skipToContent: 'Skip to content',
    breadcrumb: 'Breadcrumb',
    onThisPage: 'On this page',
    /** {date} is the formatted date. */
    publishedOn: 'Published {date}',
    nothingToFilterBy: 'Nothing to filter by yet.',
  },

  nav: {
    label: 'Site',
    articles: 'Articles',
    releaseNotes: 'Release Notes',
    manage: 'Manage',
    signIn: 'Sign in',
    signingIn: 'Signing in…',
    signOut: 'Sign out',
  },

  appearance: {
    label: 'Appearance',
    light: 'Light',
    dark: 'Dark',
  },

  language: {
    label: 'Language',
    /** The switch's name, with the language it is on. */
    current: (name: string) => `Language: ${name}`,
  },

  layout: {
    loadingKnowledgeBase: 'Loading the knowledge base…',
  },

  filters: {
    heading: 'Filters',
    searchPlaceholder: 'Search…',
    product: 'Product',
    productRule: 'Matches any selected product.',
    category: 'Category',
    categoryRule: 'Matches any selected category.',
    combinationRule:
      'An article is shown when it matches **any** of the values ticked in a list, and it must match **every** list you have ticked something in.',
    clear: (count: number) => `Clear ${count} filter${count === 1 ? '' : 's'}`,
  },

  pagination: {
    label: 'Pagination',
    previous: 'Previous',
    next: 'Next',
    range: (first: number, last: number, total: number) => `${first}–${last} of ${total}`,
  },

  browse: {
    searchLabel: 'Search articles',
    articles: 'Articles',
    promoted: 'Promoted',
    count: (total: number, grouped: boolean) =>
      `${total} article${total === 1 ? '' : 's'}${grouped ? ', grouped by product' : ''}`,
    sortBy: 'Sort by',
    sortBestMatch: 'Best match',
    sortMostRecent: 'Most recent',
    sortTitle: 'Title',
    finding: 'Finding articles…',
    nothingMatches: 'Nothing matches',
    tryBroader: 'Try removing a filter or searching for something broader.',
    noneYet: 'No articles have been published yet.',
    groupsReflectPage: 'Groups reflect this page of results.',
  },

  article: {
    loading: 'Loading the article…',
    back: 'Back to the knowledge base',
    readingTime: (minutes: number) => `${minutes} min read`,
    revision: (number: number) => `Revision ${number}`,
    related: 'Related articles',
    attachments: 'Attachments',
    download: 'Download',
    downloadFile: (fileName: string) => `Download ${fileName}`,
    /** The type an attachment list prints beside a file's size. */
    fileType: (fileName: string, contentType: string) =>
      attachmentFileTypeForName(fileName)?.label ?? contentType,
    byteSize: (bytes: number) => formatByteSize(bytes),
    /** The sidebar link to the admin editor, for an administrator. */
    edit: 'Edit this article',
  },

  audience: {
    privateFor: (name: string) => `Private: only ${name} can read this article`,
    privateForNobody: 'Private: not for everybody',
    /** Read out before the audience's name, which follows it. */
    hiddenPrivateFor: 'Private, for ',
    hiddenPrivate: 'Private',
  },

  releaseNotes: {
    searchLabel: 'Search release notes',
    heading: 'Release notes',
    count: (total: number, search: string | null) => {
      const notes = `${total} release note${total === 1 ? '' : 's'}`;
      return search ? `${notes} ${total === 1 ? 'matches' : 'match'} “${search}”` : notes;
    },
    finding: 'Finding release notes…',
    nothingOnThisPage: 'Nothing on this page',
    nothingMatches: 'Nothing matches',
    fewerPages: 'The list has fewer pages than this address asks for.',
    firstPage: 'Go to the first page',
    noMatch: (search: string, inSelectedProducts: boolean) =>
      `No release notes match “${search}”${
        inSelectedProducts ? ' in the selected products' : ''
      }. Try removing a filter or searching for something broader.`,
    noneForProducts: 'No published release note carries the selected products. Try removing a filter.',
    noneYet: 'No release notes have been published yet.',
    loading: 'Loading the release note…',
    back: 'Back to the release notes',
  },

  account: {
    signedInAs: 'Signed in as',
    unnamedAccount: 'An unnamed account',
    accessVia: (how: string) => `Access via ${how}`,
    grantedVia: {
      'app-role': 'app role',
      'groups-claim': 'group claim',
      'microsoft-graph': 'Microsoft Graph',
    } as Record<GrantedVia, string>,
    signInHint: 'Some articles are only available to signed-in readers.',
  },

  notFound: {
    title: 'That page does not exist',
    body: 'The link may be out of date, or the page may have been unpublished or moved.',
    home: 'Go to the knowledge base',
  },

  markdown: {
    videoNotEmbeddable: 'This video cannot be embedded.',
    imageNotAvailable: 'Image not available',
    imageNotAvailableAlt: (alt: string) => `Image not available: ${alt}`,
  },

  footer: {
    columns: {
      product: 'Product',
      resources: 'Resources',
      contact: 'Contact',
    },
    linkedIn: (company: string) => `${company} on LinkedIn`,
  },

  /** The cards in front of the admin pages (AdminLayout.tsx). */
  adminGate: {
    preparingSignIn: 'Preparing sign-in…',
    configErrorHelp: 'The public site is unaffected. Check the server logs and {ready}.',
    sessionExpired: 'Your session has expired - taking you back to sign-in…',
    checkingAccess: 'Checking your access…',
    slowTitle: 'This is taking longer than it should',
    slowIntro:
      'Your sign-in worked, but the server has not confirmed your access yet. That check is a single request and normally answers immediately.',
    slowLookAt: 'Worth looking at, in this order:',
    slowNetwork:
      'The browser’s network tab. A *repeating* call to {me} means the page is looping; a single pending one means the server is not answering.',
    slowHealth: '{ready} — confirms the server is up and reports whether the Entra ID settings are complete.',
    slowConsole: 'The browser console, for an MSAL error while acquiring the access token.',
    reload: 'Reload',
    signInTitle: 'Sign in to manage the knowledge base',
    signInBody:
      'Managing articles and metadata requires a work account that your directory has granted access to manage this knowledge base.',
    signInWithMicrosoft: 'Sign in with Microsoft',
    readingNeedsNoSignIn: 'Reading the knowledge base does not require signing in.',
    notConfiguredTitle: 'Sign-in is not configured yet',
    notConfiguredBody:
      'This server does not have complete Entra ID settings, so the admin interface cannot authenticate anyone. The public knowledge base is unaffected.',
    notConfiguredFix:
      'Set {tenant}, {api}, {spa} and {group} in the App Service application settings. {ready} lists exactly which ones are missing, and {doc} walks through the app registration.',
    noAccessTitle: 'You do not have access',
    noAccessNotMember:
      'Your sign-in worked, but you are not a member of the group that may manage this knowledge base.',
    noAccessAsk:
      'Ask a directory administrator to grant your account access to manage this knowledge base. Access changes take effect the next time you sign in, because it is your access token that carries them.',
    checkAgain: 'Check again',
    signInAsSomeoneElse: 'Sign in as someone else',
  },

  errors: {
    /**
     * What an error banner says. `code` is an ApiError's code, null for any other
     * error. In English the message is shown as it came - the server writes its
     * messages for a reader already.
     */
    describe: (_code: string | null, message: string) => message,
  },

  /** The admin site behind the gate: i18n/admin/. */
  admin: {
    rules: rulesEn,
    shell: shellEn,
    articles: articlesEn,
    editor: editorEn,
    releaseNotes: releaseNotesEn,
    site: siteEn,
  },
};

export type Messages = typeof en;
